import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject } from 'rxjs';

interface ChannelEntry {
  channel: BroadcastChannel | null;
  messages: Subject<unknown>;
}

/**
 * Generic cross-tab messaging on top of the BroadcastChannel API.
 * A tab never receives the messages it posted itself, only those posted by other tabs.
 */
@Injectable({
  providedIn: 'root'
})
export class CrossTabMessagingService implements OnDestroy {

  private readonly entries = new Map<string, ChannelEntry>();

  post<T>(channelName: string, message: T): void {
    this.getEntry(channelName).channel?.postMessage(message);
  }

  messages$<T>(channelName: string): Observable<T> {
    return this.getEntry(channelName).messages.asObservable() as Observable<T>;
  }

  ngOnDestroy(): void {
    this.entries.forEach(entry => {
      entry.channel?.close();
      entry.messages.complete();
    });
    this.entries.clear();
  }

  private getEntry(channelName: string): ChannelEntry {
    let entry = this.entries.get(channelName);
    if (!entry) {
      entry = {
        channel: typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(channelName),
        messages: new Subject<unknown>(),
      };
      this.listen(entry);
      this.entries.set(channelName, entry);
    }
    return entry;
  }

  private listen(entry: ChannelEntry): void {
    if (!entry.channel) {
      return;
    }
    entry.channel.addEventListener('message', (event: MessageEvent) => {
      entry.messages.next(event.data);
    });
  }
}
